import React, { useEffect, useMemo, useState } from 'react';
import { catalogService } from '../../services/catalogService';
import { CatalogItem, ImageReference } from '../types';
import CatalogItemForm from './CatalogItemForm';

type EditState = {
  isOpen: boolean;
  item: CatalogItem | null;
};

function getThumbnailUrl(images: ImageReference[] | undefined): string | undefined {
  if (!images || images.length === 0) return undefined;
  const primary = images.find(i => i.isPrimary) || images[0];
  return primary.thumbnailUrl || primary.cloudinaryUrl || primary.url;
}

const DEFAULT_NEW_ITEM: CatalogItem = {
  id: '',
  recordStatus: 'draft',
  catalogDate: new Date().toISOString(),
  cataloger: 'Unspecified',
  lastModified: new Date().toISOString(),
  completenessScore: 0,
  titleProper: 'Untitled',
  placeOfPublication: '',
  publisher: '',
  dateOfPublication: '',
  centuryOfCreation: '',
  epochOfCreation: 'Other',
  extent: '',
  objectType: 'Other',
  locationOfCreation: { country: '', city: '' },
  currentPhysicalLocation: { country: '', city: '' },
  creators: [],
  images: [],
  language: [],
  visibilityLevel: 'public',
  accessTier: 'free'
};

// Removed external paper link per request

const CollectionsCatalog: React.FC = () => {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState>({ isOpen: false, item: null });
  const [filters, setFilters] = useState({
    status: 'any' as 'any' | 'draft' | 'review' | 'published' | 'archived',
    type: 'any' as 'any' | string,
    language: '',
    yearMin: '',
    yearMax: '',
    epoch: 'any' as 'any' | 'Ancient' | 'Medieval' | 'Renaissance' | 'Early Modern' | 'Pre-Modern' | 'Other'
  });

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await catalogService.getAllCatalogItems(200);
      setItems(data);
    } catch (e) {
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (!query.trim()) {
        await loadAll();
      } else {
        const data = await catalogService.searchByTitle(query.trim());
        setItems(data);
      }
    } catch (e) {
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(item => {
      if (q) {
        const hay = `${item.titleProper || ''} ${item.publisher || ''} ${item.dateOfPublication || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.status !== 'any' && item.recordStatus !== filters.status) return false;
      if (filters.type !== 'any' && item.objectType !== filters.type) return false;
      if (filters.epoch !== 'any' && item.epochOfCreation !== filters.epoch) return false;
      if (filters.language) {
        const want = filters.language.toLowerCase();
        const langs = (item.language || []).map(l => l.toLowerCase());
        if (!langs.some(l => l.includes(want))) return false;
      }
      const year = parseInt(item.dateOfPublication || '', 10);
      const min = parseInt(filters.yearMin || '0', 10);
      const max = parseInt(filters.yearMax || '0', 10);
      if (!Number.isNaN(min) && min > 0 && !Number.isNaN(year) && year < min) return false;
      if (!Number.isNaN(max) && max > 0 && !Number.isNaN(year) && year > max) return false;
      return true;
    });
  }, [items, query, filters]);

  const openEdit = (item: CatalogItem) => setEdit({ isOpen: true, item: { ...item } });
  const closeEdit = () => setEdit({ isOpen: false, item: null });

  // Inline edit/save handlers are no longer used since the full CatalogItemForm
  // is mounted inside the drawer. All create/update logic has been delegated
  // to that component.

  const deleteCurrent = async () => {
    if (!edit.item || !edit.item.id) return;
    const ok = window.confirm('Delete this item permanently?');
    if (!ok) return;
    await catalogService.deleteCatalogItem(edit.item.id);
    closeEdit();
    await loadAll();
  };

  const addNew = () => openEdit({ ...DEFAULT_NEW_ITEM });

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">Collections Catalog</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addNew} className="px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700">Add Book</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <form onSubmit={onSearch} className="mb-4 space-y-3">
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by title..."
            className="w-full rounded border px-4 py-2"
          />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value as any }))} className="rounded border px-2 py-2 md:col-span-1 col-span-2">
              <option value="any">Any status</option>
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} className="rounded border px-2 py-2 md:col-span-1 col-span-2">
              <option value="any">Any type</option>
              <option>Manuscript</option>
              <option>Incunable</option>
              <option>Early Printed Book</option>
              <option>Map</option>
              <option>Print</option>
              <option>Scientific Drawing</option>
              <option>Artistic Drawing</option>
              <option>Illustration</option>
              <option>Handwritten Notebook</option>
              <option>Broadside</option>
              <option>Ephemera</option>
              <option>Other</option>
            </select>
            <select value={filters.epoch} onChange={e => setFilters(f => ({ ...f, epoch: e.target.value as any }))} className="rounded border px-2 py-2 md:col-span-1 col-span-2">
              <option value="any">Any time period</option>
              <option value="Ancient">Ancient</option>
              <option value="Medieval">Medieval</option>
              <option value="Renaissance">Renaissance</option>
              <option value="Early Modern">Early Modern</option>
              <option value="Pre-Modern">Pre-Modern</option>
              <option value="Other">Other</option>
            </select>
            <input value={filters.language} onChange={e => setFilters(f => ({ ...f, language: e.target.value }))} placeholder="Language" className="rounded border px-2 py-2 md:col-span-1 col-span-2" />
            <input value={filters.yearMin} onChange={e => setFilters(f => ({ ...f, yearMin: e.target.value }))} placeholder="Year min" className="rounded border px-2 py-2" />
            <input value={filters.yearMax} onChange={e => setFilters(f => ({ ...f, yearMax: e.target.value }))} placeholder="Year max" className="rounded border px-2 py-2" />
          </div>
        </form>

        {error && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 mb-4">{error}</div>}
        {loading && <div className="text-gray-600">Loading…</div>}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map(item => {
            const thumb = getThumbnailUrl(item.images);
            return (
              <button key={item.id}
                type="button"
                onClick={() => openEdit(item)}
                className="group text-left bg-white rounded shadow hover:shadow-md border overflow-hidden">
                <div className="aspect-[3/4] bg-gray-100 overflow-hidden">
                  {thumb ? (
                    <img src={thumb} alt={item.titleProper} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-gray-400 text-xs">No image</div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-sm font-medium line-clamp-2">{item.titleProper}</div>
                  {item.publisher && <div className="text-xs text-gray-500 mt-1">{item.publisher}{item.dateOfPublication ? `, ${item.dateOfPublication}` : ''}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </main>

      {edit.isOpen && edit.item && (
        <div className="fixed inset-0 z-20">
          <div className="absolute inset-0 bg-black/40" onClick={closeEdit} />
          <div className="absolute inset-y-0 right-0 w-full max-w-4xl bg-white shadow-xl overflow-auto">
            <div className="p-3 border-b flex items-center justify-between">
              <h2 className="font-semibold">Edit Catalog Item</h2>
              <div className="flex gap-2">
                {!!edit.item?.id && (
                  <button onClick={deleteCurrent} className="px-3 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50">Delete</button>
                )}
                <button onClick={closeEdit} className="px-3 py-2 rounded border">Close</button>
              </div>
            </div>
            <CatalogItemForm
              initialItem={edit.item}
              onSave={async () => {
                closeEdit();
                await loadAll();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionsCatalog;


